// frontend/src/components/ErrorBoundary.js
// Class component required — React error boundaries cannot be function components.
// Wraps the entire app so any unhandled render error shows a recovery UI
// instead of a blank white screen.
import React from 'react';
import { reportError } from '../observability';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  // Called during render when a descendant throws — updates state synchronously
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // Called after render — safe place for side effects like logging
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught a render error:', error, info.componentStack);
    reportError(error, info.componentStack);
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.icon}>!</div>
          <h2 style={styles.heading}>Something went wrong</h2>
          <p style={styles.body}>
            An unexpected error occurred. Try refreshing the page.
          </p>
          <div style={styles.buttonRow}>
            <button
              onClick={() => window.location.reload()}
              style={styles.primaryButton}
            >
              Refresh page
            </button>
            <button
              onClick={this.handleReset}
              style={styles.secondaryButton}
            >
              Try again
            </button>
          </div>

          {/* Only visible in development builds — gives engineers stack detail */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={styles.details}>
              <summary style={styles.detailsSummary}>
                Error details (dev only)
              </summary>
              <pre style={styles.pre}>
                {this.state.error.toString()}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

// Inline styles — consistent with the GeoAgent frontend convention (no Tailwind, no CSS modules)
const styles = {
  overlay: {
    minHeight: 'var(--app-height)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgb(var(--c-gray-900))',
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    maxWidth: '420px',
    width: '100%',
    background: 'rgb(var(--c-gray-800))',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    padding: '40px 32px',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  // Text-based warning indicator — avoids relying on emoji rendering across environments
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'rgb(var(--c-amber-900) / 0.4)',
    color: '#d97706',
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '16px',
  },
  heading: {
    margin: '0 0 8px 0',
    fontSize: '20px',
    fontWeight: '700',
    color: 'rgb(var(--c-gray-100))',
  },
  body: {
    margin: '0 0 28px 0',
    fontSize: '14px',
    color: 'rgb(var(--c-gray-400))',
    lineHeight: '1.5',
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap', // Stacks on very narrow screens
  },
  primaryButton: {
    padding: '10px 20px',
    background: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: '120px',
    // 44px min touch target height for mobile usability
    minHeight: '44px',
  },
  secondaryButton: {
    padding: '10px 20px',
    background: 'rgb(var(--c-gray-700))',
    color: 'rgb(var(--c-gray-200))',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: '100px',
    minHeight: '44px',
  },
  details: {
    marginTop: '24px',
    textAlign: 'left',
  },
  detailsSummary: {
    fontSize: '12px',
    color: 'rgb(var(--c-gray-500))',
    cursor: 'pointer',
  },
  pre: {
    marginTop: '8px',
    fontSize: '11px',
    color: '#dc2626',
    background: 'rgb(var(--c-red-900) / 0.4)',
    borderRadius: '6px',
    padding: '12px',
    overflowX: 'auto',
    maxHeight: '160px',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    textAlign: 'left',
  },
};

export default ErrorBoundary;
